import { handleErrors } from '../helpers';
import {
  ICertificationFilterOption,
  ICertificationStats,
  IFilterItem,
  IStatsParams,
  IAssignableUser,
} from './types';
import {
  fetchVoucherSummary,
  fetchAssignableUsers,
  assignVouchers,
} from 'contexts/voucher/services';
import { VoucherSummary } from 'contexts/voucher/types';
import { config } from 'config/config.local';
import { v4 as uuidv4 } from 'uuid';
import {
  fetchCertificationFilterOptions,
  fetchCertificationStats,
  fetchUsersCertificationsLogs,
} from 'services/certifications';
import { isRetryableError } from 'constants/errors/codes';

const VOUCHER_ASSIGNMENT_RETRY_DELAY_MS = 500;

interface GetLogsResult {
  results: any[];
  count: number;
}

export const getCertificationFilterOptionsAction = async (
  accountId: string
): Promise<ICertificationFilterOption[] | Error | undefined> => {
  try {
    const response = await fetchCertificationFilterOptions(accountId);
    return response.data;
  } catch (error) {
    return handleErrors(error);
  }
};

export const getUsersCertificationsLogsAction = async (
  filters: IFilterItem,
  business_account_id?: string,
  accountTeamIds?: Array<string>
): Promise<GetLogsResult | Error> => {
  try {
    const {
      certification_short_names,
      result,
      user_ids,
      expiration_date_range,
      assigned_date_range,
      page,
      page_size,
      ordering,
    } = filters;

    const payload = {
      business_account_id,
      certification_short_names,
      result,
      user_ids,
      team_ids: accountTeamIds?.length ? accountTeamIds : filters.teams?.map((team) => team.id),
      expiration_date_range,
      assigned_date_range,
      page,
      page_size,
      ...(ordering ? { ordering } : {}),
    };

    const response = await fetchUsersCertificationsLogs(payload);

    return {
      results: response.data.results,
      count: response.data.count,
    };
  } catch (error) {
    return handleErrors(error);
  }
};

export const getCertificationsCountersAction = async (
  payload: IStatsParams
): Promise<{ results: ICertificationStats } | Error> => {
  try {
    const response = await fetchCertificationStats(payload);
    return {
      results: response.data,
    };
  } catch (error) {
    return handleErrors(error);
  }
};

export const getVoucherSummaryAction = async (payload: {
  account_id: string;
}): Promise<{ results: VoucherSummary } | Error> => {
  try {
    const response = await fetchVoucherSummary(payload);
    return {
      results: response.data,
    };
  } catch (error) {
    return handleErrors(error);
  }
};

export const getAssignableUsersAction = async (params: {
  account_id: string;
  certification_version_ids: string[];
  team_ids?: string[];
  search?: string;
}): Promise<{ results: IAssignableUser[]; count: number } | Error> => {
  try {
    const certVersionIds = params.certification_version_ids.join(',');
    const queryParams = new URLSearchParams();
    queryParams.append('certification_version_id', certVersionIds);

    if (params.search) {
      queryParams.append('search', params.search);
    }

    const url = `${config.routes.certifications.assignableUsers(
      params.account_id
    )}?${queryParams.toString()}`;

    const response = await fetchAssignableUsers(url);

    const users: IAssignableUser[] = response.data.results.map((u: any) => ({
      id: u.uaa_id || u.id?.toString(),
      email: u.email,
      name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email.split('@')[0],
      eligible_certifications: u.eligible_certifications || [],
    }));

    return {
      results: users,
      count: response.data.count || users.length,
    };
  } catch (error) {
    return handleErrors(error);
  }
};

export const assignVouchersAction = async (params: {
  account_id: string;
  assignments: Array<{ certification_version_id: string; assignee_user_id: string }>;
}): Promise<
  | {
      success: boolean;
      totalAssigned: number;
      totalFailed: number;
      vouchersRemaining?: number;
      failedCertificationIds: string[];
      rejectedUserIds: string[];
      rejectedReasons: Array<{ userId: string; certificationId: string; reason: string }>;
      failedAssignments: Record<string, string[]>;
    }
  | Error
> => {
  try {
    const groupedAssignments = params.assignments.reduce((acc, assignment) => {
      const certId = assignment.certification_version_id;
      if (!acc[certId]) {
        acc[certId] = [];
      }
      acc[certId].push(assignment.assignee_user_id);
      return acc;
    }, {} as Record<string, string[]>);

    const url = config.routes.certifications.assignVouchers(params.account_id);

    const apiCalls = Object.entries(groupedAssignments).map(
      async ([certificationVersionId, assigneeUserIds]) => {
        const idempotencyKey = globalThis.crypto?.randomUUID?.() ?? uuidv4();
        const payload = {
          certification_version_id: certificationVersionId,
          assignee_user_ids: assigneeUserIds,
        };
        let responseData: any;
        const maxAttempts = 2;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            const response = await assignVouchers(url, payload, idempotencyKey);
            responseData = response.data;
            break;
          } catch (error) {
            const isLastAttempt = attempt === maxAttempts - 1;
            // A 4xx means the request itself is wrong; retrying it won't help, so fail fast.
            if (isLastAttempt || !isRetryableError(error)) {
              return {
                certificationVersionId,
                success: false,
                accepted_count: 0,
                rejected_count: assigneeUserIds.length,
                vouchers_remaining: undefined,
                rejected: assigneeUserIds.map((userId) => ({
                  voucher_assignment_id: '',
                  user_id: userId,
                  reason: 'request_failed',
                })),
                error,
              };
            }
            await new Promise((resolve) => setTimeout(resolve, VOUCHER_ASSIGNMENT_RETRY_DELAY_MS));
          }
        }
        return {
          certificationVersionId,
          success: responseData.rejected_count === 0,
          accepted_count: responseData.accepted_count,
          rejected_count: responseData.rejected_count,
          vouchers_remaining: responseData.vouchers_remaining,
          rejected: responseData.rejected ?? [],
        };
      }
    );

    const results = await Promise.all(apiCalls);

    const totalAssigned = results.reduce((sum, r) => sum + (r.accepted_count || 0), 0);
    const vouchersRemaining = results.find(
      (r) => r.vouchers_remaining !== undefined
    )?.vouchers_remaining;

    let success = true;
    let totalFailed = 0;
    const failedCertificationIds: string[] = [];
    const rejectedUserIds: string[] = [];
    const rejectedReasons: Array<{
      userId: string;
      certificationId: string;
      reason: string;
    }> = [];
    const failedAssignments: Record<string, string[]> = {};

    for (const r of results) {
      const rejected = r.rejected ?? [];
      // No rejections for this certification → nothing to do
      if (!rejected.length) continue;

      // At least one rejection means the overall operation is not fully successful
      success = false;
      totalFailed += r.rejected_count ?? rejected.length;
      failedCertificationIds.push(r.certificationVersionId);

      for (const rej of rejected) {
        rejectedUserIds.push(rej.user_id);
        rejectedReasons.push({
          userId: rej.user_id,
          certificationId: r.certificationVersionId,
          reason: rej.reason,
        });
        // Build the userId -> certificationIds map
        (failedAssignments[rej.user_id] ||= []).push(r.certificationVersionId);
      }
    }

    // All fields are already normalized by construction.
    // No conditional logic is needed in the return object.
    return {
      success,
      totalAssigned,
      totalFailed,
      vouchersRemaining,
      failedCertificationIds,
      rejectedUserIds,
      rejectedReasons,
      failedAssignments,
    };
  } catch (error) {
    return handleErrors(error);
  }
};
